import Http from "@/commons/Http";
import IndicadorRealBusiness from "@/business/indicadores/IndicadorRealBusiness";
import EnderecoBusiness from "@/business/EnderecoBusiness";

const path = 'api/odin';

export default {

  async buscarConsumoDetalhado(filtro, page, size) {
    return Http.post(`${path}/dashboard/consumo-detalhado?page=${page}&size=${size}`, filtro);
  },

  async paginacaoAcompanhamentoMatriculas(sort, direction, page, size, filters = {}) {
    return Http.post(`${path}/acompanhamento-matriculas/pagination?sort=${sort || 'matricula'},${direction || 'asc'}&page=${page || 1}&size=${size || 30}`, filters);
  },

  async getOcrMatricula(indicadorRealId){
    return Http.get(`${path}/acompanhamento-matriculas/${indicadorRealId}/ocr`);
  },

  /**
   * Motor de análise que atende esta matrícula:
   * {motor: 'ITN'|'LEGADO', possuiAnaliseLegada}.
   * Quem decide é o backend — a tela só obedece.
   */
  async getMotorAnaliseMatricula(indicadorRealId){
    return Http.get(`${path}/acompanhamento-matriculas/${indicadorRealId}/motor-analise`);
  },

  async capturarExtracaoAto(atoId){
    return Http.get(`${path}/ato/${atoId}/extracao`);
  },

  async compararExtracaoAto(atoId){
    return Http.get(`${path}/ato/${atoId}/extracao/comparacao`);
  },

  async realizarExtracaoAto(atoId, sobrescrever = false, validarSituacaoAnterior = true){
    return Http.post(`${path}/ato/${atoId}/extracao/${sobrescrever}/${validarSituacaoAnterior}`);
  },

  async editar(atoId, dados){
    return Http.post(`${path}/ato/${atoId}/extracao/editar`, dados);
  },

  async salvarAlteracoes(atoId){
    return Http.post(`${path}/ato/${atoId}/atualizar`);
  },

  async compararAnaliseJuridica(indicadorRealId){
    return Http.get(`${path}/ato/${indicadorRealId}/analise-juridica`);
  },

  async salvarAnaliseJuridica(indicadorRealId, dto){
    return Http.post(`${path}/ato/${indicadorRealId}/analise-juridica`, dto);
  },

  /** ********************************** **/

  preprocessarNome (nome) {
    // Converter para minúsculas
    nome = (nome|| '').toLowerCase();

    // Remover acentos
    nome = nome.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    // Remover caracteres especiais e espaços extras
    nome = nome.replace(/[^\w\s]/gi, '');

    // Remover partículas de nomes (da, de, do, das, dos, e, etc.)
    nome = nome.replace(/\b(da|de|do|das|dos|e|la|le|du|del|von|van|di|al|el|bin|bint)\b/g, ' ');

    // Remover espaços múltiplos
    nome = nome.replace(/\s+/g, ' ').trim();

    return nome;
  },

  // Implementação da distância de Levenshtein
  distanciaLevenshtein(s1, s2) {
    const m = s1.length;
    const n = s2.length;

    // Criar matriz para cálculos intermediários
    const dp = Array(m + 1).fill().map(() => Array(n + 1).fill(0));

    // Inicializar a primeira coluna e linha
    for (let i = 0; i <= m; i++) {
      dp[i][0] = i;
    }

    for (let j = 0; j <= n; j++) {
      dp[0][j] = j;
    }

    // Preencher a matriz
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (s1[i - 1] === s2[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1];
        } else {
          dp[i][j] = 1 + Math.min(
            dp[i - 1][j],     // Deleção
            dp[i][j - 1],     // Inserção
            dp[i - 1][j - 1]  // Substituição
          );
        }
      }
    }

    return dp[m][n];
  },

  calcularSimilaridade(nome1, nome2) {
    // Pré-processar os nomes
    const nome1Proc = this.preprocessarNome(nome1);
    const nome2Proc = this.preprocessarNome(nome2);

    // Calcular a distância de Levenshtein
    const distancia = this.distanciaLevenshtein(nome1Proc, nome2Proc);

    // Calcular a similaridade como percentual
    const tamanhoMaximo = Math.max(nome1Proc.length, nome2Proc.length);

    if (tamanhoMaximo === 0) {
      return 100.0;  // Ambas strings vazias são consideradas iguais
    }

    const similaridade = (1 - distancia / tamanhoMaximo) * 100;

    return similaridade;
  },

  getNegocioJuridico() {
    return [
      { "id": 0, "nome": "Abertura de Matrícula" },
      { "id": 1, "nome": "Transferência de Propriedade" },
      { "id": 2, "nome": "Criação de Restrições" },
      { "id": 3, "nome": "Retificação de Atos Anteriores" },
      { "id": 4, "nome": "Regularizações e Modificações do Imóvel" },
      { "id": 5, "nome": "Extinção ou Baixa de Restrições" },
      { "id": 6, "nome": "Qualificação das partes" },
      { "id": 7, "nome": "Averbações Administrativas ou Informativas" }
    ]
  },

  getGravames() {
    return [
      { "id": 1, "nome": "ONUS" },
      { "id": 2, "nome": "ACAO" },
      { "id": 3, "nome": "INDISPONIBILIDADE" },
      { "id": 4, "nome": "COMPROMISSO" },
      { "id": 5, "nome": "DOMINIO" },
    ]
  },

  getPapelSimplificado() {
    return [
      { "id": 1, "nome": "TRANSMITENTE", "negocio": [1] },
      { "id": 2, "nome": "ADQUIRENTE", "negocio": [0,1] },
      { "id": 3, "nome": "CREDOR", "negocio": [2,5] },
      { "id": 4, "nome": "DEVEDOR", "negocio": [2,5] },
      { "id": 5, "nome": "ANUENTE", "negocio": [] },
      { "id": 6, "nome": "REPRESENTANTE", "negocio": [] },
      // Polos do ato PROCESSUAL averbado (existência de ação, citação reipersecutória, protesto
      // contra alienação), que o pipeline da matrícula passou a extrair. `negocio` vazio: a
      // averbação da lide não é negócio de cadeia. Precisam existir AQUI, e não só no back — a
      // validação da edição manual do ato recusa papel fora desta lista.
      { "id": 7, "nome": "REQUERENTE", "negocio": [] },
      { "id": 8, "nome": "REQUERIDO", "negocio": [] },
      // Papel NEUTRO da matrícula: quem o ato nomeia sem papel aparente (o proprietário que requer
      // a averbação da construção, o credor que requer a averbação do leilão negativo). `negocio`
      // vazio, como os polos processuais: não é negócio de cadeia e não move fração.
      { "id": 9, "nome": "INTERESSADO", "negocio": [] }
    ]
  },

  getCamposIndicador() {
    return [
      { "id": "tipoImovel", "nome": "Tipo de Imóvel", "type": "select", "options": IndicadorRealBusiness.getTiposImovel() },
      { "id": "numeroUnidade", "nome": "Número da unidade", "type": "text" },

      { "id": "tipoLogradouro", "nome": "Tipo logradouro", "type": "text" },
      { "id": "nomeRua", "nome": "Logradouro (rua)", "type": "text" },
      { "id": "numeroImovel", "nome": "Número", "type": "text" },
      { "id": "complemento", "nome": "Complemento", "type": "text" },
      { "id": "bairro", "nome": "Bairro (setor)", "type": "text" },
      { "id": "cidade", "nome": "Cidade", "type": "text" },
      { "id": "estado", "nome": "Estado", "type": "select", "options": EnderecoBusiness.getEstados() },

      { "id": "condominio", "nome": "Condomínio (Empreendimento/Condomínio)", "type": "text" },
      { "id": "bloco", "nome": "Bloco", "type": "text" },
      { "id": "loteamento", "nome": "Loteamento", "type": "text" },
      { "id": "lote", "nome": "Lote", "type": "text" },
      { "id": "quadra", "nome": "Quadra", "type": "text" },

      { "id": "fracaoIdeal", "nome": "Fração solo (%)", "type": "currency" },
      { "id": "areaPrivativa", "nome": "Área Privativa (Outras áreas)", "type": "currency" },
      { "id": "areaComum", "nome": "Área Comum (Outras áreas)", "type": "currency" },
      // { "id": "areaConstruida", "nome": "Área Construída (Outras áreas)", "type": "currency" },

      { "id": "medidaArea", "nome": "Unidade de Medida (Área)", "type": "select", "options": IndicadorRealBusiness.getMedidas() },
      { "id": "areaAcrescida", "nome": "Área Acrescida (Área)", "type": "currency" },
      { "id": "areaReduzida", "nome": "Área Reduzida (Área)", "type": "currency" },
      { "id": "areaTotal", "nome": "Área total (Área)", "type": "currency" },

      { "id": "denominacao", "nome": "Denominação da Área Rural", "type": "text" },
      { "id": "zonaImovel", "nome": "Zona do Imóvel", "type": "select", "options": IndicadorRealBusiness.getCategorias() },
      { "id": "unidadeAutonoma", "nome": "Unidade Autônoma", "type": "radio" },

      { "id": "status", "nome": "Situação", "type": "select", "options": IndicadorRealBusiness.getStatusImovel(false) },

      { "id": "ccir", "nome": "CCIR", "type": "text" },
      { "id": "itrNirf", "nome": "ITR/NIRF", "type": "text" },
      { "id": "car", "nome": "CAR", "type": "text" },
      { "id": "cib", "nome": "CIB", "type": "text" },
      { "id": "certificacaoIncra", "nome": "Certificação no SIGEF", "type": "text" },
      { "id": "possuiGeorreferenciamento", "nome": "Possui Georreferenciamento", "type": "radio" },
      { "id": "georreferenciamento", "nome": "Georreferenciamento", "type": "text" },


      { "id": "registroAnterior", "nome": "Registro Anterior", "type": "text" },
      { "id": "cadastroImobiliario", "nome": "Cadastro Imobiliário", "type": "text" },
      { "id": "confrontantes", "nome": "Confrontantes (Especificações do imóvel)", "type": "text" }
    ]

  },

  promptsAtoPersonalizacao(){
    return [
      {id: 'ATO_DETALHES', nome: 'Detalhes do Ato', modelo: `
### INTERPRETAÇÃO DE TERMOS
- Quando encontrar "cessão de direitos", tratar como compra e venda
- Quando encontrar "escritura de permuta", tratar como transferência de propriedade
- Quando aparecer "constituição de renda", considerar como criação de ônus
- Se mencionar "promessa de compra", tratar como compromisso de compra e venda

### VALORES E MOEDAS
- Valores em "cruzeiros" ou "cruzados" devem ser mantidos com a moeda histórica
- Quando não houver valor mas disser "título gratuito", não buscar valor de transação

### SITUAÇÕES ESPECIAIS
- Se disser "dação em pagamento", é uma transferência de propriedade
- Quando falar "adjudicação", considerar como transferência por decisão judicial
      `},
      {id: 'ATO_INDICADOR_REAL', nome: 'Indicador Real', modelo: `
### CLASSIFICAÇÃO DE IMÓVEIS
- "Prédio" no nosso cartório sempre significa casa
- "Gleba" deve ser classificado como terreno rural
- "Unidade" sozinha significa apartamento
- "Edícula" deve ser tratada como casa

### ENDEREÇOS E LOCALIZAÇÃO
- "Distrito de" deve ir no campo de bairro
- "Localidade" é o mesmo que bairro
- "Zona rural" sempre classificar o imóvel como rural
- "Prolongamento" no nome indica loteamento, não bairro

### ÁREAS E MEDIDAS
- "Braças" deve ser convertido para metros (1 braça = 2,2 metros)

### LOCALIZAÇÕES CONHECIDAS
- "Kobrasol" é sempre bairro, nunca loteamento
- "Pedra Branca" no nosso município é loteamento
- "Centro" é sempre bairro
- Qualquer coisa com "Jardim" no nome é loteamento
      `},
      {id: 'ATO_QUALIFICACAO', nome: 'Qualificação Geral'},
      {id: 'ATO_ENVOLVIDOS_ABERTURA', nome: 'Envolvidos - Abertura de Matrícula', modelo: `
### IDENTIFICAÇÃO DE PROPRIETÁRIOS
- Quando disser "e sua mulher" sem citar o nome dela, procurar o nome em outro lugar do texto
- Se aparecer "Espólio de João", o proprietário é apenas "João"
- Quando mencionar "representado por", não incluir o representante como proprietário
- "Anuência de" significa que a pessoa NÃO é proprietária

### DIVISÃO DE PROPRIEDADE
- Casal sem menção de frações: sempre 50% para cada
- Vários irmãos sem frações: dividir igualmente
- "Condomínio entre" significa propriedade compartilhada igualmente
      `},
      {id: 'ATO_ENVOLVIDOS_TRANSFERENCIA', nome: 'Envolvidos - Transferências', modelo : `
### CÔNJUGES E CASAIS
- Em regime de comunhão parcial, incluir o cônjuge apenas quando explicitamente mencionado como comprador
- Quando disser "casados em separação de bens", nunca incluir automaticamente o cônjuge
- Se falar "com recursos próprios de fulano", apenas fulano deve ser o adquirente
- Quando mencionar "meação", dividir igualmente entre o casal

### HERDEIROS E INVENTÁRIO
- Em inventário, só incluir quem for explicitamente nomeado como herdeiro
- Quando disser "deixa a meação para o cônjuge", o cônjuge recebe 50%
- Se falar "único herdeiro", essa pessoa recebe 100%

### FRAÇÕES E PARTES
- "Metade ideal" significa 50%
- "Terça parte" significa um terço
- "Parte ideal" sem especificação significa divisão igual entre todos
- Quando não especificar percentuais, dividir igualmente
      `},
      {id: 'ATO_ENVOLVIDOS_GRAVAME', nome: 'Envolvidos - Gravames'},
    ]
  },

  promptsItnPersonalizacao(){
    return [
      {id: 'ANALISE_ATOS', nome: 'Análise dos Atos', modelo: `
### INTERPRETAÇÃO DE TERMOS
- Quando encontrar "cessão de direitos", tratar como compra e venda
- Quando aparecer "constituição de renda", considerar como criação de ônus
- Se mencionar "promessa de compra", tratar como compromisso de compra e venda

### SITUAÇÕES ESPECIAIS
- Se disser "dação em pagamento", é uma transferência de propriedade
- Quando falar "adjudicação", considerar como transferência por decisão judicial
      `},
      {id: 'INDICADOR_REAL', nome: 'Indicador Real', modelo: `
### CLASSIFICAÇÃO DE IMÓVEIS
- "Prédio" no nosso cartório sempre significa casa
- "Gleba" deve ser classificado como terreno rural
- "Edícula" deve ser tratada como casa

### ENDEREÇOS E LOCALIZAÇÃO
- "Distrito de" deve ir no campo de bairro
- "Localidade" é o mesmo que bairro
- "Zona rural" sempre classificar o imóvel como rural

### LOCALIZAÇÕES CONHECIDAS
- "Centro" é sempre bairro
- Qualquer coisa com "Jardim" no nome é loteamento
      `},
    ]
  },

  textosAjudaInformacaoErro(){
    return {
      'OCR_IMAGEM_MATRICULA' : `
        <ul class="mb-0">
            <li>
                <strong>Documentos manuscritos:</strong> O sistema de OCR possui limitações para reconhecimento de textos escritos à mão. Recomendamos atenção redobrada na revisão desses documentos.
            </li>
            <li>
                <strong>Caracteres duvidosos:</strong> Quando houver dúvida sobre letras ou números rasurados/ilegíveis, o sistema inserirá <code>[?]</code> no texto. Por favor, substitua esses marcadores pelo conteúdo correto.
            </li>
            <li>
                <strong>Primeira página da Matrícula:</strong> O sistema adiciona automaticamente a data de abertura no cabeçalho do texto transcrito, garantindo essa informação essencial para o processamento posterior.
            </li>
        </ul>
      `,
    }
  },

  tratarStatus(status = '') {
    if (status.toLowerCase() === 'inativa') return 'ENCERRADA'
    if (status.toLowerCase() === 'ativa') return 'ABERTA'
    return status;
  },

  getFormatters() {
    return [
      {label: 'Abertura', key: 'abertura', type: 'datetime'},
      {label: 'Destinação', key: 'zonaImovel', type: 'enum', lista: IndicadorRealBusiness.getCategorias()},
      {label: 'Unidade Autônoma', key: 'unidadeAutonoma', type: 'boolean'},
      {label: 'Tipo de Imóvel', key: 'tipoImovel', type: 'enum', lista: IndicadorRealBusiness.getTiposImovel()},

      {label: 'Tipo logradouro', key: 'tipoLogradouro'},
      {label: 'Logradouro', key: 'nomeRua'},
      {label: 'Número', key: 'numeroImovel'},
      {label: 'Bairro', key: 'bairro'},
      {label: 'Cidade', key: 'cidade'},
      {label: 'Estado', key: 'estado'},
      {label: 'Complemento', key: 'complemento'},
      {label: 'Bloco', key: 'bloco'},
      {label: 'Lote', key: 'lote'},
      {label: 'Quadra', key: 'quadra'},
      {label: 'Área Privativa', key: 'areaPrivativa'},
      {label: 'Área Comum', key: 'areaComum'},
      {label: 'Área Edificada', key: 'areasEdificada'},
      {label: 'Área total (Área)', key: 'areaTotal'},
      {label: 'Fração solo (%)', key: 'fracaoIdeal'},
      {label: 'Empreendimento/Condomínio', key: 'condominio'},
      {label: 'Loteamento', key: 'loteamento'},
      {label: 'Denominação da Área Rural', key: 'denominacao'},
      {label: 'Cadastro Imobiliário', key: 'cadastroImobiliario'},
      {label: 'Certificação no SIGEF', key: 'certificacaoIncra'},
      {label: 'INCRA (CCIR)', key: 'ccir'},
      {label: 'ITR', key: 'itrNirf'},
      {label: 'CAR', key: 'car'},
      {label: 'Cadastro Imobiliário Brasileiro (CIB)', key: 'cib'},
      {label: 'Medida Área', key: 'medidaArea', type: 'enum', lista: IndicadorRealBusiness.getMedidas()},
      {label: 'Área total', key: 'area'},
      {label: 'Possui Georreferenciamento', key: 'possuiGeorreferenciamento', type:'boolean'},
      {label: 'Georreferenciamento', key: 'georreferenciamento'},
      {label: 'Especificações do Imóvel', key: 'confrontantes'},
      {label: 'Registro Anterior', key: 'registroAnterior'},

      {label: 'Situação', key: 'status', type: 'enum', lista: IndicadorRealBusiness.getStatusImovel(false)},
    ];
  },

}
